import React, { useEffect, useMemo, useState } from 'react'
import { CheckBox, Loader, NVButton, Select } from '@nv/basic-components'
import { SearchBar } from '@nv/basic-components'
import DownloadFileIcon from '@nv/images/design/download-file-icon'
import DropdownArrowIcon from '@nv/images/design/dropdown-arrow-icon'
import FunnelIcon from '@nv/images/design/funnel-icon'
import RefreshIcon from '@nv/images/design/refresh-icon'
import TableSortIcon from '@nv/images/design/table-sort-icon'
import { flexRender, getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import clsx from 'clsx'
import debounce from 'lodash/debounce'
import { useTranslation } from 'react-i18next'
import { ActionDropdown } from '../action_dropdown/ActionDropdown'
import Pagination from './components/pagination/Pagination'
import styles from './ListTable.module.scss'

export const ListTable = ({
  columns,
  data,
  currentPageNum = 0,
  dataLength = 0,
  loading = true,
  showFilter = false,
  onPageChange,
  download,
  setFilterModal,
  refreshList,
  expandedContent,
  numRowsPerPage = 10,
  searchPlaceholder = 'main.search',
  searchValue,
  paginationLabel = 'main.rows',
  noDataLabel = 'main.noData',
  storageKey,
  onNumRowsChange,
  multiRowActions = [],
  onSearch,
  searchableStrings,
  isSimpleTable = false,
}: any) => {
  const { t } = useTranslation()
  const [sorting, setSorting] = useState(() => (storageKey ? JSON.parse(localStorage.getItem(storageKey)) : []))
  const [newNumRowsPerPage, setNewNumRowsPerPage] = useState(numRowsPerPage)
  const [expandedRows, setExpandedRows] = useState([])
  const [selectedRows, setSelectedRows] = useState([])

  const [selectedSearchField, setSelectedSearchField] = useState(searchableStrings?.[0] || '') // this needs to move to parent
  const [searchInput, setSearchInput] = useState(searchValue)

  const [globalFilter, setGlobalFilter] = useState('')

  const debouncedSearch = useMemo(
    () =>
      debounce((term) => {
        onSearch(term)
      }, 300),
    [],
  )

  const checkboxColumn = multiRowActions?.length
    ? [
        {
          id: 'select',
          header: () => (
            <CheckBox
              classes={{ container: styles['multi-actions-container'] }}
              field='select-all'
              checked={selectedRows.length === data.length && data.length > 0}
              onChange={() => {
                if (selectedRows.length === data.length) setSelectedRows([])
                else setSelectedRows((data = Array.isArray(data) ? data : []))
              }}
              role='checkbox'
              tabIndex={0}
              square={true}
            />
          ),
          cell: ({ row }) => (
            <CheckBox
              classes={{ container: styles['multi-actions-container'] }}
              field={`select-row-${row.id}`}
              checked={selectedRows.includes(row.id)}
              onChange={() => {
                if (selectedRows.includes(row.id)) setSelectedRows((prev) => prev.filter((id) => id !== row.id))
                else setSelectedRows((prev) => [...prev, row.id])
              }}
              role='checkbox'
              tabIndex={0}
              square={true}
            />
          ),
        },
      ]
    : []

  useEffect(() => {
    storageKey && localStorage.setItem(storageKey, JSON.stringify(sorting))
  }, [sorting])

  const table = !isSimpleTable
    ? useReactTable({
        data,
        columns: [...checkboxColumn, ...(columns || [])],
        state: {
          sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
      })
    : useReactTable({
        data,
        columns,
        state: { globalFilter },
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        // Client-side search:
        getFilteredRowModel: getFilteredRowModel(),
      })

  const toggleRowExpand = (rowId) => {
    setExpandedRows((prev) => {
      if (prev.includes(rowId)) return prev.filter((id) => id !== rowId)
      else return [...prev, rowId]
    })
  }

  return (
    <>
      <div className={styles['table-container']}>
        <div className={styles['actionbar']}>
          <div className={styles['search-container']}>
            {searchableStrings?.length > 0 && (
              <Select
                label={t('admin.electorsList.searchBy')}
                name='searchField'
                value={selectedSearchField}
                onChange={(value) => setSelectedSearchField(value)}
                style={{ container: { width: 150, marginRight: 10 } }}
                options={searchableStrings}
              />
            )}

            {(onSearch || isSimpleTable) && (
              <SearchBar
                placeholder={t(`${searchPlaceholder}`)}
                label={selectedSearchField.label || ''}
                name='search'
                onChange={(e) => {
                  setSearchInput(e.target.value)
                  if (isSimpleTable) {
                    setGlobalFilter(e.target.value)
                  } else {
                    debouncedSearch(e.target.value)
                  }
                }}
                style={{ container: { width: 275, marginRight: 25 } }}
                value={searchInput}
              />
            )}
          </div>

          <div className={styles['actionbar__icons']}>
            {multiRowActions?.length > 0 && selectedRows.length >= 2 && (
              <div className={styles['multi-actions']}>
                <ActionDropdown
                  values={multiRowActions.map((action) => ({
                    label: action.label,
                    value: action.value,
                  }))}
                  onCallback={async (selected) => {
                    const selectedData = selectedRows.map((id) => data[parseInt(id, 10)])

                    try {
                      console.log('Selected action:', selected)
                      console.log('Selected Rows:', selectedData)
                      //await selectedFn(selectedData)
                    } catch (err) {
                      console.error('Multi action error:', err)
                    }
                  }}
                  ariaLabel={t('admin.dropdown.actionOptions', { value: 'selected rows' })}
                />
              </div>
            )}

            {refreshList && (
              <NVButton
                variant='iconOnly'
                icon={<RefreshIcon />}
                onClick={refreshList}
                ariaLabel={t('buttons.refresh')}
                classes={{ container: styles['refresh-btn'] }}
              />
            )}
            {showFilter && (
              <NVButton variant='iconOnly' icon={<FunnelIcon />} onClick={() => setFilterModal(true)} ariaLabel={t('buttons.filters')} />
            )}
            {download && (
              <NVButton variant='iconOnly' icon={<DownloadFileIcon />} onClick={() => download()} ariaLabel={t('buttons.download')} />
            )}
          </div>
        </div>

        {loading ? (
          <div className='spinner-container'>
            <Loader />
          </div>
        ) : !data.length ? (
          <div className={styles['empty-list']} role='alert' aria-live='assertive'>
            {t(noDataLabel)}
          </div>
        ) : (
          <div className={styles['table-scroller']}>
            <table className={styles['table']}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      if (header.id === 'expand') return <td key={header.id} aria-hidden='true' />
                      return (
                        <th
                          key={header.id}
                          onClick={storageKey ? header.column.getToggleSortingHandler() : null}
                          className={clsx(styles['sortable-header'], {
                            [styles['action-col']]: header.column.id === 'action',
                          })}
                        >
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getIsSorted() === 'asc' && <TableSortIcon className={styles['sort-asc']} />}
                          {header.column.getIsSorted() === 'desc' && <TableSortIcon className={styles['sort-desc']} />}
                        </th>
                      )
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <React.Fragment key={row.id}>
                    <tr className={styles['tr']}>
                      {row.getVisibleCells().map((cell: any) => {
                        const colDef = cell.column.columnDef
                        const isExpand = cell.getContext().column.id === 'expand'
                        const tdClass = clsx({
                          [styles['wrap-column']]: !colDef.ellipsis,
                          [styles['ellipsis-column']]: !!colDef.ellipsis,
                          [styles['expand-move']]: isExpand,
                        })
                        return isExpand ? (
                          <React.Fragment key={cell.id}>
                            <th className={clsx(styles['mobile-header'], styles['expand-move'])}>
                              {flexRender(colDef.header, cell.getContext())}
                            </th>
                            <td key={`expand-btn-${row.id}`} className={tdClass}>
                              <NVButton
                                onClick={() => toggleRowExpand(row.id)}
                                icon={
                                  <DropdownArrowIcon
                                    className={clsx({ [styles['expand-btn__expanded']]: expandedRows.includes(row.id) })}
                                  />
                                }
                                classes={{ container: styles['expand-btn'] }}
                                ariaLabel={expandedRows.includes(row.id) ? 'Collapse row' : 'Expand row'}
                              />
                            </td>
                          </React.Fragment>
                        ) : (
                          <React.Fragment key={cell.id}>
                            <th className={styles['mobile-header']}>{flexRender(colDef.header, cell.getContext())}</th>
                            <td key={cell.id} className={tdClass}>
                              {flexRender(colDef.cell, cell.getContext())}
                            </td>
                          </React.Fragment>
                        )
                      })}
                    </tr>

                    {expandedRows.includes(row.id) && (
                      <tr>
                        <td colSpan={row.getVisibleCells().length + 1} className={styles['expanded__content']}>
                          {expandedContent(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>

              <tfoot>
                {table.getFooterGroups().some((footerGroup) => footerGroup.headers.some((header) => header.column.columnDef.footer)) &&
                  table.getFooterGroups().map((footerGroup) => (
                    <tr key={footerGroup.id}>
                      {footerGroup.headers.map((header) => (
                        <th key={header.id}>
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {dataLength !== 0 && (
        <Pagination
          currentPageNum={currentPageNum}
          dataLength={dataLength}
          onPageChange={onPageChange}
          numRowsPerPage={newNumRowsPerPage}
          paginationLabel={paginationLabel}
          // not sure why we have both of these below
          setNewNumRowsPerPage={setNewNumRowsPerPage}
          onNumRowsChange={onNumRowsChange}
        />
      )}
    </>
  )
}
